import type { EdgeType } from "./buildAccessModel";
import { NODE_HEIGHT, NODE_WIDTH, type NodePosition } from "./layoutGraph";

// Source is always in a column left of its target (principals → scopes →
// derived spaces), so we anchor source-right to target-left. Lines run flush to
// the card edges; each card's 3px surface-colored ring (box-shadow) paints over
// the ends, leaving the same small gap as the step map.
export function edgePath(source: NodePosition, target: NodePosition): string {
  const x1 = source.x + NODE_WIDTH;
  const y1 = source.y + NODE_HEIGHT / 2;
  const x2 = target.x;
  const y2 = target.y + NODE_HEIGHT / 2;
  const dx = Math.max((x2 - x1) * 0.5, 24);
  return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
}

export type EdgeSegment = { d: string; type: EdgeType };

// An inherited grant is really two hops: the member reaches the scope only
// because a group they're in has the grant. Route the dashed inherited line to
// that group, then hand off to a solid direct leg for the group's own grant —
// so the group's direct grant is never drawn as if the group had inherited it.
// Falls back to a single straight hop when the group's position is unavailable.
export function inheritedEdgeSegments(
  source: NodePosition,
  group: NodePosition | undefined,
  target: NodePosition
): EdgeSegment[] {
  if (!group) return [{ d: edgePath(source, target), type: "inherited" }];
  return [
    { d: edgePath(source, group), type: "inherited" },
    { d: edgePath(group, target), type: "direct" },
  ];
}
